'use client';

import React, { useState, useEffect } from 'react';
import { Cookie, X, ChevronDown, ChevronUp, ExternalLink, Shield } from 'lucide-react';
import Link from 'next/link';

const CONSENT_KEY = 'ledgerpay_cookie_consent';
const CONSENT_VERSION = '1';

interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  version: string;
  timestamp: string;
}

/** Read the stored consent object — returns null if not yet set */
export function getStoredConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConsentState;
  } catch {
    return null;
  }
}

/** Returns true only when analytics consent has been explicitly granted */
export function hasAnalyticsConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.analytics === true;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) {
        // Show banner after short delay for better UX
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
      const parsed: ConsentState = JSON.parse(stored);
      // Re-show if version changed
      if (parsed.version !== CONSENT_VERSION) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const saveConsent = (analyticsAllowed: boolean, marketingAllowed: boolean) => {
    const consent: ConsentState = {
      necessary: true,
      analytics: analyticsAllowed,
      marketing: marketingAllowed,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
      // Dispatch event so GoogleAnalytics component can react without a page reload
      window.dispatchEvent(new CustomEvent('ledgerpay:consent', { detail: consent }));
    } catch {}
    setVisible(false);
  };

  const handleAcceptAll = () => saveConsent(true, true);
  const handleRejectAll = () => saveConsent(false, false);
  const handleSavePreferences = () => saveConsent(analytics, marketing);

  if (!mounted || !visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="true"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
    >
      <div className="max-w-2xl mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Cookie size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">We use cookies</h2>
              <p className="text-xs text-muted-foreground mt-0.5">GDPR &amp; CCPA compliant</p>
            </div>
          </div>
          <button
            onClick={handleRejectAll}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            aria-label="Reject all and close"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            LedgerPay uses cookies to keep you signed in, remember your preferences, and improve our service.
            Strictly necessary cookies are always active. Analytics cookies (Google Analytics) are only loaded
            after you give consent.{' '}
            <Link href="/privacy" className="text-primary hover:underline inline-flex items-center gap-0.5">
              Privacy Policy <ExternalLink size={10} />
            </Link>
          </p>

          {/* Expandable details */}
          <button
            onClick={() => setShowDetails(v => !v)}
            className="flex items-center gap-1.5 mt-3 text-xs font-semibold text-primary hover:underline"
          >
            {showDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showDetails ? 'Hide' : 'Manage'} cookie preferences
          </button>

          {showDetails && (
            <div className="mt-3 space-y-3 border border-border rounded-xl p-4">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield size={13} className="text-success" />
                    <p className="text-xs font-semibold text-foreground">Strictly Necessary</p>
                    <span className="text-xs text-success font-medium">Always active</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Authentication, session management, security. Cannot be disabled.
                  </p>
                </div>
                <div className="w-9 h-5 rounded-full bg-success flex-shrink-0 mt-1 cursor-not-allowed opacity-60" />
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 pt-3 border-t border-border">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-foreground">Analytics</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Google Analytics — helps us understand how LedgerPay is used so we can improve it.
                    <strong className="block mt-0.5 text-foreground/70">Not loaded until you consent.</strong>
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 mt-1 ${analytics ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${analytics ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4 pt-3 border-t border-border">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-foreground">Marketing</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Used to personalize ads and measure campaign effectiveness. We do not sell your data.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={marketing}
                  onClick={() => setMarketing(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 mt-1 ${marketing ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${marketing ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center gap-2 px-5 pb-5">
          <button
            onClick={handleRejectAll}
            className="w-full sm:w-auto flex-1 py-2 px-4 text-xs font-semibold border border-border rounded-lg hover:bg-muted transition-colors text-foreground"
          >
            Reject Optional
          </button>
          {showDetails && (
            <button
              onClick={handleSavePreferences}
              className="w-full sm:w-auto flex-1 py-2 px-4 text-xs font-semibold border border-primary/30 bg-primary/5 text-primary rounded-lg hover:bg-primary/10 transition-colors"
            >
              Save Preferences
            </button>
          )}
          <button
            onClick={handleAcceptAll}
            className="w-full sm:w-auto flex-1 py-2 px-4 text-xs font-bold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Accept All Cookies
          </button>
        </div>
      </div>
    </div>
  );
}
